import { useEffect } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { SelectField } from "./SelectField";
import { cn } from "@/lib/utils";

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

const PaginationControls = ({
  page = 1,
  pageSize = 10,
  total = 0,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions = [10, 30, 50],
  disabled = false,
  labels = {},
  listLabel = null,
  hideWhenSinglePage = false,
  className,
}) => {
  const { t } = useTranslation("common");

  const defaultLabels = {
    itemsPerPage: t("common.itemsPerPage", "Items per page"),
    previous: t("common.previous", "Previous"),
    next: t("common.next", "Next"),
    pageLabel: ({ page, totalPages }) =>
      t("common.pageLabel", {
        page,
        totalPages,
        defaultValue: `Page ${page} of ${totalPages}`,
      }),
  };

  const mergedLabels = { ...defaultLabels, ...labels };
  const totalPages = Math.max(1, Math.ceil(total / pageSize) || 1);
  const currentPage = clamp(page, 1, totalPages);
  const canGoPrev = currentPage > 1;
  const canGoNext = currentPage < totalPages;

  useEffect(() => {
    if (page !== currentPage && onPageChange) onPageChange(currentPage);
  }, [page, currentPage, onPageChange]);

  const pageSizeValue = String(pageSize);
  const sizeOptions = Array.from(new Set(pageSizeOptions)).sort(
    (a, b) => a - b,
  );

  const handlePageChange = (nextPage) => {
    if (!onPageChange) return;
    onPageChange(clamp(nextPage, 1, totalPages));
  };

  const handlePageSizeChange = (nextValue) => {
    if (!onPageSizeChange) return;
    const nextSize = Number(nextValue);
    if (!Number.isNaN(nextSize)) {
      onPageSizeChange(nextSize);
    }
  };

  if (hideWhenSinglePage && totalPages <= 1) return null;

  return (
    <div
      role={listLabel ? "group" : undefined}
      aria-label={listLabel || undefined}
      className={cn(
        "flex flex-col gap-3 text-sm text-muted-foreground sm:flex-row sm:items-center sm:justify-between",
        className,
      )}
    >
      <div className="flex w-full items-center justify-between gap-2 sm:w-auto sm:justify-start">
        {listLabel ? (
          <span className="font-medium text-foreground">{listLabel}</span>
        ) : null}
        <span>{mergedLabels.itemsPerPage}</span>
        <SelectField
          value={pageSizeValue}
          onValueChange={handlePageSizeChange}
          options={sizeOptions}
          selectClassName="w-[90px]"
          className="space-y-0"
          disabled={disabled}
        />
      </div>

      <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row sm:items-center sm:gap-3">
        <span className="text-center sm:text-left">
          {mergedLabels.pageLabel({ page: currentPage, totalPages })}
        </span>
        <div className="flex w-full items-center gap-2 sm:w-auto">
          <Button
            variant="outline"
            size="sm"
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={disabled || !canGoPrev}
            className="flex-1 sm:flex-none"
          >
            <ChevronLeft className="h-4 w-4" />
            {mergedLabels.previous}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={disabled || !canGoNext}
            className="flex-1 sm:flex-none"
          >
            {mergedLabels.next}
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
};

export default PaginationControls;
